import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import Divider from "@mui/material/Divider";
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import classnames from "classnames";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import _ from "lodash";
import {useCalcContext} from "../../contexts/CalcContext";
import {CostCalculator} from "../../components/CostCalculator";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {useModalsStore} from "../../stores/modalsStore";
import {BanksGridRebatesModal} from "../DealPage/BanksGridRebatesModal";

export const CalcPagePricing = ({ deskDeal, createNewPurchase, getRebates }) => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const [isCreatingLease, setIsCreatingLease] = useState(false)
    const [isCreatingPurchase, setIsCreatingPurchase] = useState(false)

    const navigate = useNavigate()
    const openModal = useModalsStore((state) => state.openModal)

    const {
        calcState,
        setCalcState,
        carState,
        isUsed
    } = useCalcContext()

    const isLoading = (isCreatingPurchase || isCreatingLease)

    const dealRouteId = calcState.base_car_id || 'preview'

    const openRebatesAndThen = (mode, navigateTo) => {
        openModal({
            element: BanksGridRebatesModal,
            id: 'BanksGridRebatesModal',
            props: {
                mode,
                onApply: () => navigate(navigateTo),
            },
        })
    }

    const handleLease   = () => openRebatesAndThen('lease',   `/quotes/${dealRouteId}`)
    const handleFinance = () => openRebatesAndThen('finance', `/finance-quote/${dealRouteId}`)
    const handleCash    = () => navigate(`/finance-quote/${dealRouteId}`)

    // USER FEEDBACK #1 – Card 3 must never be disabled regardless of Card 1 selection
    const isInputDisabled = false
    const isDisabled = false

    // Guard against NaN when fields are empty
    const sellingPrice = _.round((Number(calcState.cost) || 0) + (Number(calcState.profit) || 0), 2)
    const clearCostFields = () => {
        const fieldValues = {
            invoice_plus: '',
            invoice_minus: '',
            msrp_plus: '',
            msrp_minus: '',
            msrp_percentage: ''
        }
        setCalcState({...calcState, ...fieldValues})
    }

    return (
        <Box className={classnames("panel", {
            mobile: isTabletOrMobile
        })}>
            <div className="panel-header">
                <div className="panel-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16, height:16}}>
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                        <line x1="7" y1="7" x2="7.01" y2="7"/>
                    </svg>
                    Vehicle Pricing
                </div>
            </div>
            <div className="panel-body">
                <div>
                    {
                        !isUsed &&
                        <React.Fragment>
                            <div className={'pricing-row'}>
                                <span className={'pricing-label'}>MSRP</span>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading
                                    })}
                                    placeholder={'0'}
                                    value={calcState.msrp}
                                    onChange={(value) => setCalcState({...calcState, msrp: value})}
                                    allowNull={true}
                                    disabled={isInputDisabled}
                                />
                            </div>
                            <div className={'pricing-row'}>
                                <span className={'pricing-label'}>Invoice</span>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading
                                    })}
                                    prefix="$"
                                    value={calcState.invoice}
                                    onChange={(value) => setCalcState({...calcState, invoice: value})}
                                    disabled={isInputDisabled}
                                />
                            </div>
                        </React.Fragment>
                    }
                    {
                        isUsed &&
                        <React.Fragment>
                            <div className={'pricing-row'}>
                                <span className={'pricing-label'}>Book Value</span>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading
                                    })}
                                    prefix="$"
                                    value={calcState.msrp}
                                    onChange={(value) => {
                                        setCalcState({...calcState, invoice: value, msrp: value})
                                    }}
                                    disabled={isInputDisabled}
                                />
                            </div>
                            <div className={'pricing-row'}>
                                <span className={'pricing-label cpo-label'}>
                                    Certified Pre-Owned: <strong className="cpo-yes">{calcState.is_certified_preowned ? 'Yes' : 'No'}</strong>
                                </span>
                                <div className="pricing-input-wrap" style={{background:'transparent', borderColor:'transparent', justifyContent:'flex-end'}}>
                                    <label className="mswitch">
                                        <input
                                            type="checkbox"
                                            checked={calcState.is_certified_preowned}
                                            onChange={() => setCalcState({
                                                ...calcState,
                                                is_certified_preowned: !calcState.is_certified_preowned
                                            })}
                                        />
                                        <span className="mswitch-track"></span>
                                        <span className="mswitch-thumb"></span>
                                    </label>
                                </div>
                            </div>
                            <div className={'pricing-row'}>
                                <span className={'pricing-label'}>Current Mileage</span>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading
                                    })}
                                    prefix="$"
                                    value={calcState.current_mileage}
                                    onChange={(value) => setCalcState({...calcState, current_mileage: value})}
                                    disabled={isInputDisabled}
                                    InputProps={{}}
                                />
                            </div>
                        </React.Fragment>
                    }
                    <div className={'pricing-row'}>
                        <span className={'pricing-label'}>
                            Cost
                            <span className="cost-icon">
                                <CostCalculator state={calcState} setState={setCalcState} isLoading={isLoading} />
                            </span>
                        </span>
                        <CustomCurrencyInput
                            className={classnames('payment-row_value', {
                                loading: isLoading
                            })}
                            placeholder={'0'}
                            value={calcState.cost}
                            onChange={(value) => setCalcState({...calcState, cost: value})}
                            onKeyDown={clearCostFields}
                            allowNull={true}
                            disabled={isInputDisabled}
                        />
                    </div>
                    <div className={'pricing-row'}>
                        <span className={'pricing-label'}>Profit</span>
                        <CustomCurrencyInput
                            className={classnames('payment-row_value', {
                                loading: isLoading
                            })}
                            prefix="$"
                            placeholder={calcState.profit}
                            value={calcState.profit}
                            onChange={(value) => setCalcState({...calcState, profit: value})}
                            disabled={isInputDisabled}
                        />
                    </div>
                    <Divider className={'divider'} sx={{marginBottom: '10px'}}/>
                    <div className={'selling-price-row'}>
                        <span className={'selling-price-label'}>Selling Price</span>
                        <div className="pricing-input-wrap disabled">
                            <span className="currency-prefix">$</span>
                            <span style={{flex:1, textAlign:'right', fontSize:'13px', fontWeight:600, color:'var(--text-primary)', paddingRight:'10px'}}>{sellingPrice}</span>
                        </div>
                    </div>
                    <Divider className={'divider'}/>
                </div>
                <div className="action-buttons">
                    <Tooltip title="Press Enter to Calculate" arrow placement={'bottom'}>
                        <span>
                            <Button
                                className={classnames('action-btn active-lease', {
                                    isDisabled: isDisabled
                                })}
                                onClick={handleLease}
                                disabled={isDisabled}
                            >
                                {isCreatingLease ? 'Calculating...' : 'Lease'}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        className={classnames('action-btn inactive-finance', {
                            isDisabled: isDisabled
                        })}
                        onClick={handleFinance}
                        disabled={isDisabled}
                    >
                        {isCreatingPurchase ? 'Calculating...' : 'Finance'}
                    </Button>
                    <Button
                        className={classnames('action-btn inactive-cash', {
                            isDisabled: isDisabled
                        })}
                        onClick={handleCash}
                        disabled={false}
                    >
                        {isCreatingPurchase ? 'Calculating...' : 'Cash'}
                    </Button>
                </div>
            </div>
        </Box>
    )
}
